import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Map, Layers, MapPin } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/Table';
import { toast } from 'sonner';
import { Badge } from '../components/Badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/Dialog';
import { Button } from '../components/Button';
const layers = ['Sentimen', 'Ancaman', 'Demo / Aksi', 'Hoaks', 'Kunjungan VVIP'];
const regions = [{
  region: 'Jawa',
  tension: 88,
  level: 'KRITIS',
  x: '25%',
  y: '60%',
  issue: 'Harga beras, hoaks bansos, pajak hiburan'
}, {
  region: 'Sumatera',
  tension: 72,
  level: 'WASPADA',
  x: '12%',
  y: '40%',
  issue: 'Kelangkaan pupuk, sentimen ekonomi'
}, {
  region: 'Kalimantan',
  tension: 48,
  level: 'STABIL',
  x: '40%',
  y: '35%',
  issue: 'IKN — polarisasi terukur'
}, {
  region: 'Sulawesi',
  tension: 58,
  level: 'WASPADA',
  x: '62%',
  y: '40%',
  issue: 'Hilirisasi nikel — kritik lingkungan'
}, {
  region: 'Bali-Nusa',
  tension: 65,
  level: 'WASPADA',
  x: '50%',
  y: '70%',
  issue: 'Pajak hiburan 75% — komunitas pariwisata'
}, {
  region: 'Maluku',
  tension: 32,
  level: 'STABIL',
  x: '76%',
  y: '50%',
  issue: 'Stabil'
}, {
  region: 'Papua',
  tension: 78,
  level: 'TINGGI',
  x: '85%',
  y: '55%',
  issue: 'Keamanan kunjungan VVIP 25 Mei'
}];
const provinces = [{
  name: 'DKI Jakarta',
  score: 82,
  issues: 5,
  hot: 'Pajak hiburan, IKN'
}, {
  name: 'Jawa Tengah',
  score: 88,
  issues: 8,
  hot: 'Banjir Demak, harga beras'
}, {
  name: 'Jawa Timur',
  score: 75,
  issues: 6,
  hot: 'Konflik suporter, MBG'
}, {
  name: 'Jawa Barat',
  score: 78,
  issues: 5,
  hot: 'Demo buruh UMP'
}, {
  name: 'Sumatera Utara',
  score: 62,
  issues: 4,
  hot: 'Kelangkaan pupuk'
}, {
  name: 'Bali',
  score: 70,
  issues: 3,
  hot: 'Pajak hiburan 75%'
}, {
  name: 'Papua',
  score: 78,
  issues: 4,
  hot: 'Kunjungan VVIP'
}, {
  name: 'Sulawesi Tengah',
  score: 58,
  issues: 3,
  hot: 'Hilirisasi nikel'
}];
const levelColor: Record<string, string> = {
  KRITIS: 'bg-red-500 border-red-500',
  TINGGI: 'border-red-500 bg-red-500/30',
  WASPADA: 'bg-amber-500 border-amber-500',
  STABIL: 'bg-emerald-500 border-emerald-500'
};
export function Geospatial() {
  const [active, setActive] = useState<string[]>(['Sentimen', 'Ancaman']);
  const [selectedRegion, setSelectedRegion] = useState<(typeof regions)[0] | null>(null);
  const toggle = (l: string) => setActive(a => a.includes(l) ? a.filter(x => x !== l) : [...a, l]);
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-1846">
      <PageHeader group="Stabilitas Nasional" title="Geospatial Issue Intelligence" description="Peta isu nasional dengan layer sentimen, ancaman, dan aktivitas — provinsi & kota hotspot." icon={Map} data-id="element-1847" />

      <Card className="bg-card/50 border-border/50" data-id="element-1848">
        <CardContent className="p-4" data-id="element-1849">
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-id="element-1850">
            <Layers className="size-4 text-muted-foreground" data-id="element-1851" />
            <span className="font-mono text-[10px] uppercase text-muted-foreground mr-2" data-id="element-1852">
              LAYERS:
            </span>
            {layers.map(l => <button key={l} onClick={() => toggle(l)} className={`font-mono text-[10px] px-2 py-1 rounded border transition ${active.includes(l) ? 'bg-primary/15 text-primary border-primary/40' : 'bg-muted/30 text-muted-foreground border-border/50'}`} data-id="element-1853">
                {l}
              </button>)}
          </div>

          <div className="relative h-[400px] bg-black/40 rounded border border-border/30 overflow-hidden" data-id="element-1854">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" data-id="element-1855" />
            {regions.map(r => <button key={r.region} className="absolute group" style={{
            left: r.x,
            top: r.y,
            transform: 'translate(-50%, -50%)'
          }} onClick={() => setSelectedRegion(r)} data-id="element-1856">
                <div className={`relative size-5 rounded-full border-2 ${levelColor[r.level]}`} data-id="element-1857">
                  {r.tension >= 70 && <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" data-id="element-1858" />}
                </div>
                <div className="absolute top-6 left-1/2 -translate-x-1/2 text-center whitespace-nowrap" data-id="element-1859">
                  <p className="font-mono text-[10px] font-bold" data-id="element-1860">{r.region}</p>
                  <p className="font-mono text-[9px] text-muted-foreground" data-id="element-1861">
                    {r.tension}
                  </p>
                </div>
              </button>)}

            <div className="absolute bottom-2 left-2 flex flex-col gap-1 bg-background/80 p-2 border border-border/50 rounded backdrop-blur text-[9px] font-mono" data-id="element-1862">
              <div className="flex items-center gap-2" data-id="element-1863">
                <span className="size-2 rounded-full bg-red-500" data-id="element-1864" /> KRITIS
                (&gt;80)
              </div>
              <div className="flex items-center gap-2" data-id="element-1865">
                <span className="size-2 rounded-full bg-amber-500" data-id="element-1866" /> WASPADA
                (60-80)
              </div>
              <div className="flex items-center gap-2" data-id="element-1867">
                <span className="size-2 rounded-full bg-emerald-500" data-id="element-1868" /> STABIL
                (&lt;60)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card/50 border-border/50" data-id="element-1869">
        <CardContent className="p-0" data-id="element-1870">
          <div className="px-4 py-3 border-b border-border/50" data-id="element-1871">
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground" data-id="element-1872">
              Tabel Provinsi Hotspot
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-1873">
            <Table data-id="element-1874">
              <TableHeader className="bg-muted/20" data-id="element-1875">
                <TableRow className="border-border/50 hover:bg-transparent" data-id="element-1876">
                  <TableHead className="font-mono text-[9px]" data-id="element-1877">
                    PROVINSI
                  </TableHead>
                  <TableHead className="font-mono text-[9px]" data-id="element-1878">
                    TENSION SCORE
                  </TableHead>
                  <TableHead className="font-mono text-[9px]" data-id="element-1879">
                    JUMLAH ISU
                  </TableHead>
                  <TableHead className="font-mono text-[9px]" data-id="element-1880">
                    ISU UTAMA
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-1881">
                {provinces.map(p => <TableRow key={p.name} className="border-border/30 hover:bg-muted/10" data-id="element-1882">
                    <TableCell className="text-xs font-bold flex items-center gap-2" data-id="element-1883">
                      <MapPin className="size-3 text-muted-foreground" data-id="element-1884" />
                      {p.name}
                    </TableCell>
                    <TableCell data-id="element-1885">
                      <div className="flex items-center gap-2" data-id="element-1886">
                        <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden" data-id="element-1887">
                          <div className={`h-full ${p.score >= 80 ? 'bg-red-500' : p.score >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{
                        width: `${p.score}%`
                      }} data-id="element-1888" />
                        </div>
                        <span className="font-mono text-xs" data-id="element-1889">{p.score}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs" data-id="element-1890">
                      {p.issues}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground" data-id="element-1891">
                      {p.hot}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedRegion} onOpenChange={open => !open && setSelectedRegion(null)} data-id="element-1892">
        <DialogContent className="sm:max-w-[425px] border-border/50 bg-background/95 backdrop-blur" data-id="element-1893">
          <DialogHeader data-id="element-1894">
            <div className="flex items-center justify-between" data-id="element-1895">
              <DialogTitle className="font-mono text-sm uppercase tracking-wider text-foreground flex items-center gap-2" data-id="element-1896">
                <MapPin className="size-4 text-primary" data-id="element-1897" />
                {selectedRegion?.region}
              </DialogTitle>
              {selectedRegion && <Badge variant="outline" className={`font-mono text-[9px] h-5 border-none ${selectedRegion.level === 'KRITIS' ? 'bg-red-500/10 text-red-500' : selectedRegion.level === 'TINGGI' ? 'bg-red-500/10 text-red-500' : selectedRegion.level === 'WASPADA' ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`} data-id="element-1898">
                  {selectedRegion.level} ({selectedRegion.tension})
                </Badge>}
            </div>
            <DialogDescription className="text-xs" data-id="element-1899">
              Detail intelijen geospasial wilayah {selectedRegion?.region}.
            </DialogDescription>
          </DialogHeader>

          {selectedRegion && <div className="grid gap-4 py-4" data-id="element-1900">
              <div className="rounded-md border border-border/50 bg-muted/20 p-3" data-id="element-1901">
                <h4 className="text-xs font-bold text-foreground mb-1" data-id="element-1902">
                  Isu Utama
                </h4>
                <p className="text-[10px] text-muted-foreground" data-id="element-1903">
                  {selectedRegion.issue}
                </p>
              </div>

              <div data-id="element-1904">
                <h4 className="text-xs font-bold text-foreground mb-2" data-id="element-1905">
                  Aktor Aktif
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-1906">
                  <Badge variant="outline" className="font-mono text-[9px] bg-muted/30" data-id="element-1907">
                    @aktivis_lokal
                  </Badge>
                  <Badge variant="outline" className="font-mono text-[9px] bg-muted/30" data-id="element-1908">
                    @suara_daerah
                  </Badge>
                  <Badge variant="outline" className="font-mono text-[9px] bg-muted/30" data-id="element-1909">
                    @info_terkini
                  </Badge>
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-md p-3" data-id="element-1910">
                <h4 className="font-mono text-[10px] font-bold text-primary uppercase tracking-wider mb-2" data-id="element-1911">
                  Rekomendasi Tindakan
                </h4>
                <ul className="space-y-1.5" data-id="element-1912">
                  <li className="flex items-start gap-2 text-[10px] text-foreground/90" data-id="element-1913">
                    <span className="mt-1 size-1.5 rounded-full bg-primary shrink-0" data-id="element-1914" />
                    Tingkatkan pemantauan pada platform TikTok dan X.
                  </li>
                  <li className="flex items-start gap-2 text-[10px] text-foreground/90" data-id="element-1915">
                    <span className="mt-1 size-1.5 rounded-full bg-primary shrink-0" data-id="element-1916" />
                    Koordinasi dengan aparat keamanan setempat.
                  </li>
                </ul>
              </div>

              <p className="font-mono text-[9px] text-muted-foreground text-right" data-id="element-1917">
                Update Terakhir:{' '}
                {new Date().toLocaleTimeString('id-ID', {
              hour: '2-digit',
              minute: '2-digit'
            })}{' '}
                WIB
              </p>
            </div>}

          <DialogFooter data-id="element-1918">
            <Button variant="outline" size="sm" onClick={() => setSelectedRegion(null)} data-id="element-1919">
              Tutup
            </Button>
            <Button variant="default" size="sm" onClick={() => {
            toast.success('Dossier dibuka', {
              description: `Memuat data lengkap untuk ${selectedRegion?.region}...`
            });
            setSelectedRegion(null);
          }} data-id="element-1920">
              Buka Dossier Lengkap
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}